import { ExclamationIcon } from "@heroicons/react/outline";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { toast } from "react-hot-toast";
import { useState } from "react";

import { Badge, IconButton, type BadgeProps, Menu } from "ui";
import type { Flow } from "./types";
import { ProgramArrowIcon } from "../Icons";
import AlertModal from "../AlertModal";
import { RoutingResource } from "../../features/routing/services/routing.service";
import { parseErrorsToArray } from "../../utils/parseErrors";
import CheckoutConfirmationWarning from "../../features/checkout-settings/components/CheckoutConfirmationWarning";
import EntityCard from "../EntityCard";
import { CommandSquareIcon, PayInProviderIcon, UserIcon } from "icons/outline";
import Link from "next/link";

type FlowCardProps = {
  flow: Flow;
  onUpdate: () => void;
};

const statusBadgeVariantMap: Record<string, BadgeProps["variant"]> = {
  DRAFT: "default",
  PUBLISHED: "primary",
  INACTIVE: "default",
};

export default function FlowCard({ flow, onUpdate }: FlowCardProps) {
  const { id, name, accounts, status } = flow;

  return (
    <EntityCard
      icon={<CommandSquareIcon className="h-5 w-5" />}
      imageDimensions={72}
      badge={
        <Badge
          variant={statusBadgeVariantMap[status]}
          className="py-[1px] capitalize"
        >
          {status.toLowerCase()}
        </Badge>
      }
      title={name}
      id={id}
      primaryAction={
        <EntityCard.PrimaryButton
          render={<Link href={`/flows/${id}/routing`} />}
        >
          View flow
        </EntityCard.PrimaryButton>
      }
      menu={<FlowsActionsMenu flow={flow} onUpdate={onUpdate} />}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foundations-subtle md:py-[7px]">
        <div className="flex items-center gap-2">
          <PayInProviderIcon
            width={16}
            height={16}
            className="shrink-0 text-foundations-subtler"
          />

          <span className="whitespace-nowrap">Pay-In</span>
        </div>

        {accounts.length && (
          <div className="flex items-center gap-2">
            <UserIcon
              width={16}
              height={16}
              className="shrink-0 text-foundations-subtler"
            />

            <span className="line-clamp-1 text-ellipsis">
              {accounts
                .slice(0, 2)
                .map((account) => account.name)
                .join(", ")}

              {accounts.length > 2 && ` and ${accounts.length - 2} more`}
            </span>
          </div>
        )}
      </div>
    </EntityCard>
  );
}

const FlowsActionsMenu = ({ flow, onUpdate }: FlowCardProps) => {
  const [confirmModalOpened, setConfirmModalOpened] = useState<
    "publish" | "delete" | "warn" | null
  >(null);
  const [warningConfirmationDescription, setWarningConfirmationDescription] =
    useState("");

  const handlePublishFlow = ({ skipValidation = false }) => {
    RoutingResource.publishFlow(flow.id, { skipValidation })
      .then(() => {
        toast.success(`Successfully published ${flow.name}`, {
          id: `flow_publish_${flow.name}`,
          duration: 4000,
        });
        onUpdate();
      })
      .catch((error) => {
        const errors = parseErrorsToArray(error);
        const [errorMsg] = errors;

        if (errors.includes("418")) {
          setWarningConfirmationDescription(errorMsg);
          setConfirmModalOpened("warn");
        } else {
          toast.error(errorMsg ?? "Error publishing flow", {
            id: "routing",
            duration: 4000,
          });
        }
      });
  };

  return (
    <>
      <Menu>
        <Menu.Trigger
          render={
            <IconButton
              variant="secondary"
              aria-label="flow settings"
              className="p-[6px]"
            />
          }
        >
          <DotsVerticalIcon aria-hidden="true" />
        </Menu.Trigger>

        <Menu.Popup align="start">
          <Menu.Item
            disabled={flow.status === "PUBLISHED"}
            onClick={() => setConfirmModalOpened("publish")}
          >
            Publish flow
          </Menu.Item>

          <Menu.Item
            className="group"
            render={
              <Link href={`/flows/${flow.id}/checkout-settings`}>
                Go to dynamic checkout
              </Link>
            }
          ></Menu.Item>

          <Menu.Item
            variant="destructive"
            onClick={() => setConfirmModalOpened("delete")}
          >
            Delete flow
          </Menu.Item>
        </Menu.Popup>
      </Menu>
      <AlertModal
        isOpen={confirmModalOpened === "delete"}
        onClose={() => setConfirmModalOpened(null)}
        variant="danger"
        Icon={ExclamationIcon}
        title="Delete flow"
        description={`Are you sure you want to delete ${flow.name}? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={async () => {
          return RoutingResource.deleteFlow(flow.id).then(() => {
            toast.success(`Successfully deleted ${flow.name}`, {
              id: `flow_delete_${flow.name}`,
              duration: 4000,
            });
            onUpdate();
          });
        }}
      />
      <AlertModal
        isOpen={confirmModalOpened === "publish"}
        onClose={() => setConfirmModalOpened(null)}
        variant="success"
        Icon={ProgramArrowIcon}
        title="Publish flow"
        description={`Are you sure you want to publish ${flow.name}?`}
        confirmLabel="Publish"
        onConfirm={() => handlePublishFlow({ skipValidation: false })}
      />
      <CheckoutConfirmationWarning
        isOpen={confirmModalOpened === "warn"}
        onClose={() => setConfirmModalOpened(null)}
        description={warningConfirmationDescription}
        onConfirm={() => {
          setConfirmModalOpened(null);
          handlePublishFlow({ skipValidation: true });
        }}
      />
    </>
  );
};
